import React from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Helmet } from 'react-helmet-async';
import { FileText, Scale, Users, CreditCard, AlertTriangle, Shield, Gavel, Mail } from 'lucide-react';

export default function TermsPage() {
  const effectiveDate = "January 11, 2026";

  return (
    <PublicLayout>
      <Helmet>
        <title>Terms & Conditions | Kosha Tax Consultants</title>
        <meta name="description" content="Read the Terms and Conditions for using Kosha Tax Consultants services." />
      </Helmet>

      <div className="bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-4 text-foreground">Terms & Conditions</h1>
            <p className="text-center text-muted-foreground mb-8">Effective Date: {effectiveDate}</p>
            
            <Card className="shadow-lg">
              <CardContent className="prose prose-lg dark:prose-invert max-w-none p-6 md:p-8">
                {/* Introduction */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Introduction</h2>
                  </div>
                  <p>
                    These Terms and Conditions ("Terms") govern your use of the services provided by 
                    <strong> Kosha Tax Consultants</strong>, a DBA (doing business as) of <strong>Annapurna Events LLC</strong>, 
                    a Texas limited liability company (collectively, "we," "us," "our," or the "Company").
                  </p>
                  <p>
                    By accessing or using our website and services, you agree to be bound by these Terms. 
                    If you do not agree to these Terms, please do not use our services.
                  </p>
                </section>

                {/* Acceptance of Terms */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Acceptance of Terms</h2>
                  <p>
                    By creating an account, uploading documents, or using any of our services, you acknowledge that 
                    you have read, understood, and agree to be bound by these Terms and our{' '}
                    <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>.
                  </p>
                </section>

                {/* Eligibility */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Eligibility</h2>
                  </div>
                  <p>
                    You must be at least 18 years of age to use our services. By using our services, you represent 
                    and warrant that you are at least 18 years old and have the legal capacity to enter into these Terms.
                  </p>
                </section>

                {/* Service Description */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Description of Services</h2>
                  <p>Kosha Tax Consultants provides tax preparation support services, including but not limited to:</p>
                  <ul>
                    <li>Individual and business tax return preparation</li>
                    <li>Tax planning consultation</li>
                    <li>Document collection and organization</li>
                    <li>Tax filing support for all U.S. states</li>
                    <li>LLC formation assistance</li>
                  </ul>
                  <p>
                    We strive to provide accurate and thorough tax preparation services; however, 
                    <strong> we do not guarantee specific outcomes, refund amounts, or acceptance by tax authorities</strong>.
                  </p>
                </section>

                {/* Important Disclaimers */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Important Disclaimers</h2>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4 not-prose">
                    <p className="font-medium mb-2">Not Tax or Legal Advice</p>
                    <p className="text-sm text-muted-foreground">
                      The information and services provided by Kosha Tax Consultants are for tax preparation support only 
                      and do not constitute tax advice, legal advice, or professional accounting advice. For complex tax 
                      situations, we recommend consulting with a licensed CPA, tax attorney, or enrolled agent.
                    </p>
                  </div>
                  <p>
                    While our team uses professional judgment in preparing your returns, <strong>you are ultimately 
                    responsible for the accuracy and completeness of the information you provide to us</strong>. 
                    We rely on the documents and information you submit.
                  </p>
                  <p className="mt-4">
                    For complete information about limitations of liability, IRS disclosures, and indemnification, 
                    please review our{' '}
                    <a href="/disclaimer" className="text-primary hover:underline font-medium">
                      Professional Services Disclaimer
                    </a>.
                  </p>
                </section>

                {/* User Responsibilities */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Shield className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">User Responsibilities</h2>
                  </div>
                  <p>As a user of our services, you agree to:</p>
                  <ul>
                    <li>Provide accurate, complete, and truthful information</li>
                    <li>Upload only documents you have the legal right to share</li>
                    <li>Not upload any illegal, fraudulent, or unauthorized content</li>
                    <li>Maintain the confidentiality of your account credentials</li>
                    <li>Notify us immediately of any unauthorized access to your account</li>
                    <li>Use our services only for lawful purposes</li>
                    <li>Not attempt to interfere with or disrupt our services or systems</li>
                  </ul>
                </section>

                {/* Document Upload */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Document Upload Responsibility</h2>
                  <p>By uploading documents to our platform, you represent and warrant that:</p>
                  <ul>
                    <li>You have the legal right and authority to upload and share such documents</li>
                    <li>The documents do not contain any illegal or fraudulent information</li>
                    <li>You consent to our processing of such documents for tax preparation purposes</li>
                  </ul>
                  <p className="font-medium">Do not upload documents you do not own or have permission to share.</p>
                </section>

                {/* Payments */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Payments</h2>
                  </div>
                  <p>
                    Payment for services is due according to the pricing displayed at the time of service selection. 
                    By submitting payment, you authorize us to charge your selected payment method.
                  </p>
                  <ul>
                    <li>All prices are in U.S. dollars unless otherwise stated</li>
                    <li>Payments are processed securely by Stripe</li>
                    <li>We do not store your full credit card details</li>
                    <li>Refunds are subject to our <Link to="/refunds" className="text-primary hover:underline">Billing & Refund Policy</Link></li>
                  </ul>
                </section>

                {/* Limitation of Liability */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Scale className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Limitation of Liability</h2>
                  </div>
                  <p className="uppercase text-sm">
                    TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, KOSHA TAX CONSULTANTS AND ANNAPURNA EVENTS LLC 
                    SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                    INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES.
                  </p>
                  <p className="uppercase text-sm mt-4">
                    OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR YOUR USE OF OUR 
                    SERVICES SHALL NOT EXCEED THE TOTAL AMOUNT OF FEES YOU HAVE PAID TO US IN THE TWELVE (12) MONTHS 
                    PRECEDING THE CLAIM.
                  </p>
                </section>

                {/* Disclaimer of Warranties */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Disclaimer of Warranties</h2>
                  <p className="uppercase text-sm">
                    OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
                    EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS 
                    FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT OUR SERVICES WILL BE 
                    UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
                  </p>
                </section>

                {/* Termination */}
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold">Termination</h2>
                  <p>
                    We reserve the right to suspend or terminate your access to our services at any time, with or 
                    without cause, and with or without notice. You may terminate your account at any time by 
                    contacting us. Upon termination, your right to use our services will immediately cease.
                  </p>
                </section>

                {/* Governing Law */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Gavel className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Governing Law & Jurisdiction</h2>
                  </div>
                  <p>
                    These Terms shall be governed by and construed in accordance with the laws of the State of Texas, 
                    United States, without regard to its conflict of law provisions. Any disputes arising from these 
                    Terms or your use of our services shall be resolved exclusively in the state or federal courts 
                    located in Texas, unless prohibited by applicable law.
                  </p>
                </section>

                {/* Contact Information */}
                <section className="mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <h2 className="text-2xl font-semibold m-0">Contact Us</h2>
                  </div>
                  <p>If you have any questions about these Terms, please contact us:</p>
                  <div className="bg-muted/50 p-4 rounded-lg not-prose">
                    <p className="font-medium">Kosha Tax Consultants</p>
                    <p className="text-muted-foreground">A DBA of Annapurna Events LLC (Texas)</p>
                    <p className="text-muted-foreground">Email: help@koshatax.com</p>
                  </div>
                </section>

                {/* Changes to Terms */}
                <section>
                  <h2 className="text-2xl font-semibold">Changes to These Terms</h2>
                  <p>
                    We reserve the right to modify these Terms at any time. When we make changes, we will update the 
                    "Effective Date" at the top of this page. Your continued use of our services after changes are 
                    posted constitutes acceptance of the updated Terms.
                  </p>
                </section>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
